import React, { useEffect, useRef, useState } from "react";
import { fileUrl, fmtDate, fmtDateTime } from "./complaint";
import "./photoUpload.css";

const ACCEPT = "image/png, image/gif, image/jpeg";

const formatSize = (bytes = 0) =>
    bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export const MAX_PHOTOS = 20;

/**
 * Multi-photo picker. Choosing files only previews them; the screen uploads
 * them when the officer presses Upload. `photos` is
 * [{ id, file, preview, status: "ready" | "uploading" | "done" | "failed", error }].
 */
export const PhotoPicker = ({ photos, onAdd, onRemove, disabled }) => {
    const inputRef = useRef(null);
    const room = MAX_PHOTOS - photos.length;

    const pick = (e) => {
        const chosen = Array.from(e.target.files || []);
        if (chosen.length) onAdd(chosen.slice(0, Math.max(0, room)), chosen.length > room);
        e.target.value = "";
    };

    const input = <input ref={inputRef} type="file" accept={ACCEPT} multiple onChange={pick} disabled={disabled} hidden />;

    if (!photos.length) {
        return (
            <label className={`ofc-drop${disabled ? " is-disabled" : ""}`}>
                <span className="ofc-drop__icon"><i className="fa fa-camera" /></span>
                <strong>Choose inspection photos</strong>
                <span>Select one or more · JPG, PNG or GIF · up to {MAX_PHOTOS} at a time</span>
                {input}
            </label>
        );
    }

    return (
        <div className="ofc-photos">
            <ul className="ofc-photos__grid">
                {photos.map((p) => (
                    <li key={p.id} className={`ofc-photos__item is-${p.status}`}>
                        <a href={p.preview} target="_blank" rel="noopener noreferrer" className="ofc-photos__img">
                            <img src={p.preview} alt={p.file.name} />
                        </a>
                        {p.status === "uploading" && <span className="ofc-photos__state"><i className="fa fa-spinner fa-spin" /></span>}
                        {p.status === "done" && <span className="ofc-photos__state is-done"><i className="fa fa-check" /></span>}
                        {p.status === "failed" && (
                            <span className="ofc-photos__state is-failed" title={p.error || "Upload failed"}><i className="fa fa-exclamation" /></span>
                        )}
                        {(p.status === "ready" || p.status === "failed") && !disabled && (
                            <button type="button" className="ofc-photos__remove" onClick={() => onRemove(p.id)} aria-label={`Remove ${p.file.name}`}>
                                <i className="fa fa-times" />
                            </button>
                        )}
                        <span className="ofc-photos__name" title={p.file.name}>{p.file.name}</span>
                        <span className="ofc-photos__meta">{p.status === "failed" ? (p.error || "Failed") : formatSize(p.file.size)}</span>
                    </li>
                ))}
                {room > 0 && !disabled && (
                    <li>
                        <button type="button" className="ofc-photos__add" onClick={() => inputRef.current?.click()}>
                            <i className="fa fa-plus" />
                            <span>Add more</span>
                        </button>
                    </li>
                )}
            </ul>
            {input}
        </div>
    );
};

export const UploadGuidelines = ({ steps }) => (
    <aside className="ofc-card ofc-guide">
        <h3 className="ofc-card__title">How to upload</h3>
        <ol className="ofc-guide__steps">
            {steps.map((s) => <li key={s}>{s}</li>)}
        </ol>
    </aside>
);

const Thumb = ({ src }) => {
    const [broken, setBroken] = useState(false);
    if (!src || broken) {
        return <span className="ofc-hist__thumb ofc-hist__thumb--empty"><i className="fa fa-picture-o" /></span>;
    }
    return (
        <a className="ofc-hist__thumb" href={src} target="_blank" rel="noopener noreferrer">
            <img src={src} alt="" loading="lazy" onError={() => setBroken(true)} />
        </a>
    );
};

const PAGE_SIZE = 20;

/**
 * The signed-in officer's own uploads, newest first. The server limits the list
 * to this officer; `fetchPage(params)` calls the history API and `refreshKey`
 * reloads it after an upload.
 */
export const UploadHistory = ({ title, itemLabel, fetchPage, blocks = [], refreshKey }) => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(null);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [block, setBlock] = useState("");

    const load = async (nextPage) => {
        setLoading(true);
        const params = { page: nextPage, limit: PAGE_SIZE };
        if (block) params.block = block;
        const res = await fetchPage(params);
        const data = res?.status ? res.data : null;
        setRows((prev) => (nextPage === 0 ? data?.rows || [] : [...prev, ...(data?.rows || [])]));
        setTotal(data ? data.total : 0);
        setPage(nextPage);
        setLoading(false);
    };

    useEffect(() => {
        load(0);
    }, [block, refreshKey]);

    const more = total !== null && rows.length < total;

    return (
        <section className="ofc-card ofc-hist">
            <div className="ofc-card__head">
                <div>
                    <h3 className="ofc-card__title">{title}</h3>
                    <span className="ofc-card__meta">
                        {total === null ? "Loading…" : `${total.toLocaleString("en-IN")} photo${total === 1 ? "" : "s"} uploaded by you`}
                    </span>
                </div>
                <div className="ofc-hist__filters">
                    {blocks.length > 1 && (
                        <select className="ofc-input" value={block} onChange={(e) => setBlock(e.target.value)} aria-label="Filter by block">
                            <option value="">All blocks</option>
                            {blocks.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                    )}
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="ofc-empty">
                    <i className="fa fa-history" />
                    <strong>{loading ? "Loading…" : "No uploads yet"}</strong>
                    {!loading && <span>Photos you upload will be listed here.</span>}
                </div>
            ) : (
                <>
                    <div className="ofc-table-scroll ofc-hide-mobile">
                        <table className="ofc-table ofc-hist__table">
                            <thead>
                                <tr>
                                    <th>Photo</th>
                                    <th>{itemLabel}</th>
                                    <th>Block</th>
                                    <th>Photo date</th>
                                    <th>Uploaded on</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id}>
                                        <td><Thumb src={fileUrl(r.imagePath)} /></td>
                                        <td>
                                            <span className="ofc-hist__name">{r.name || "—"}</span>
                                            <span className="ofc-hist__code">{r.code}</span>
                                        </td>
                                        <td>{r.block || "—"}</td>
                                        <td className="ofc-hist__nowrap">{fmtDate(r.photoDate)}</td>
                                        <td className="ofc-hist__nowrap">{fmtDateTime(r.uploadedAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <ul className="ofc-rows ofc-hist__list">
                        {rows.map((r) => (
                            <li key={r.id} className="ofc-hist__item">
                                <Thumb src={fileUrl(r.imagePath)} />
                                <div>
                                    <strong>{r.name || "—"}</strong>
                                    <span>{[r.block, r.code].filter(Boolean).join(" · ")}</span>
                                    <span>Photo {fmtDate(r.photoDate)} · uploaded {fmtDateTime(r.uploadedAt)}</span>
                                </div>
                            </li>
                        ))}
                    </ul>

                    {more && (
                        <div className="ofc-hist__more">
                            <button type="button" className="ofc-btn ofc-btn--ghost" onClick={() => load(page + 1)} disabled={loading}>
                                {loading ? "Loading…" : `Show more (${(total - rows.length).toLocaleString("en-IN")} left)`}
                            </button>
                        </div>
                    )}
                </>
            )}
        </section>
    );
};

/** react-select styles matching .ofc-input, with the menu above the mobile tab bar. */
export const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 40,
        borderRadius: 8,
        borderColor: state.isFocused ? "#c8102e" : "#d1d5db",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(200,16,46,0.1)" : "none",
        "&:hover": { borderColor: state.isFocused ? "#c8102e" : "#9ca3af" },
        fontSize: 14,
    }),
    option: (base, state) => ({
        ...base,
        fontSize: 14,
        backgroundColor: state.isSelected ? "#c8102e" : state.isFocused ? "#fef2f2" : "#fff",
        color: state.isSelected ? "#fff" : "#111827",
    }),
    placeholder: (base) => ({ ...base, color: "#9ca3af" }),
    menuPortal: (base) => ({ ...base, zIndex: 1100 }),
};
